import { z } from 'zod';

// =============================================================================
// Base block schema — every exercise block must have these fields
// =============================================================================

export const BlockBaseSchema = z.object({
  id: z.string().uuid(),
  type: z.enum([
    'guided_conversation',
    'vocabulary_grid',
    'fill_in_the_blank',
    'translation_challenge',
    'conjugation_spotlight',
    'reading_comprehension',
    'matching_exercise',
    'scenario_prompts',
    'grammar_explanation',
  ]),
  order: z.number().int().min(1),
  title: z.string().min(1).max(200),
  titleNative: z.string().min(1).max(200),
  theme: z.string().min(1).max(100),
  estimatedPages: z.number().min(0.5).max(4),
});

// =============================================================================
// 1. Guided Conversation
// =============================================================================

export const ConversationTurnSchema = z.object({
  number: z.number().int().min(1),
  role: z.enum(['other', 'learner']),
  roleName: z.string().min(1).max(50),
  lineTarget: z.string().min(1),
  lineNative: z.string().min(1),
  isPrompt: z.boolean(),
  starterHint: z.string().optional(),
});

export const GuidedConversationBlockSchema = BlockBaseSchema.extend({
  type: z.literal('guided_conversation'),
  situation: z.object({
    description: z.string().min(10),
    descriptionNative: z.string().min(10),
    emoji: z.string().min(1).max(4),
  }),
  turns: z.array(ConversationTurnSchema).min(6).max(12),
  vocabularyItems: z
    .array(
      z.object({
        target: z.string().min(1),
        native: z.string().min(1),
      }),
    )
    .min(2)
    .max(8),
  tip: z
    .object({
      emoji: z.string().min(1).max(4),
      text: z.string().min(10),
    })
    .optional(),
})
  .refine(
    (block) => {
      // At least 3 learner turns (isPrompt: true)
      const learnerTurns = block.turns.filter((t) => t.isPrompt);
      return learnerTurns.length >= 3;
    },
    { message: 'Guided conversation must have at least 3 learner prompt turns' },
  )
  .refine(
    (block) => {
      // Turns must alternate and start with 'other'
      return block.turns[0]?.role === 'other';
    },
    { message: 'Conversation must start with the other speaker' },
  );

// =============================================================================
// 2. Vocabulary Grid
// =============================================================================

export const VocabularyGridItemSchema = z
  .object({
    target: z.string().min(1),
    native: z.string().min(1),
    hideTarget: z.boolean(),
    hideNative: z.boolean(),
    gender: z.string().max(10).optional(),
    example: z.string().optional(),
  })
  .refine((item) => !(item.hideTarget && item.hideNative), {
    message: 'Cannot hide both target and native — learner needs at least one visible',
  })
  .refine(
    (item) => item.hideTarget || item.hideNative || true,
    // Some rows are fully visible (reference rows). This is valid.
  );

export const VocabularyGridBlockSchema = BlockBaseSchema.extend({
  type: z.literal('vocabulary_grid'),
  instructions: z.string().min(10),
  instructionsNative: z.string().min(10),
  items: z.array(VocabularyGridItemSchema).min(8).max(18),
}).refine(
  (block) => {
    // At least 40% of items must have a hidden cell
    const hiddenCount = block.items.filter((i) => i.hideTarget || i.hideNative).length;
    return hiddenCount >= block.items.length * 0.4;
  },
  {
    message: 'At least 40% of vocabulary items must have a hidden cell for the learner to fill in',
  },
);

// =============================================================================
// 3. Fill in the Blank
// =============================================================================

export const FillInTheBlankSentenceSchema = z.object({
  text: z
    .string()
    .min(10)
    .refine((t) => t.includes('___'), {
      message: 'Sentence text must contain "___" for the blank',
    }),
  hint: z.string().min(5),
  answer: z.string().min(1),
});

export const FillInTheBlankBlockSchema = BlockBaseSchema.extend({
  type: z.literal('fill_in_the_blank'),
  instructions: z.string().min(10),
  instructionsNative: z.string().min(10),
  wordBank: z.array(z.string().min(1)).min(5).max(12),
  sentences: z.array(FillInTheBlankSentenceSchema).min(5).max(10),
})
  .refine(
    (block) => {
      // Every sentence answer must exist in the word bank
      return block.sentences.every((s) =>
        block.wordBank.some((w) => w.toLowerCase() === s.answer.toLowerCase()),
      );
    },
    { message: 'Every sentence answer must appear in the word bank' },
  )
  .refine(
    (block) => {
      // Word bank and sentence count must match (each word used exactly once)
      return block.wordBank.length === block.sentences.length;
    },
    { message: 'Word bank size must equal sentence count (each word used exactly once)' },
  );

// =============================================================================
// 4. Translation Challenge
// =============================================================================

export const TranslationItemSchema = z.object({
  source: z.string().min(3),
  answer: z.string().min(3),
  difficulty: z.enum(['easy', 'medium', 'hard']),
});

export const TranslationChallengeBlockSchema = BlockBaseSchema.extend({
  type: z.literal('translation_challenge'),
  instructions: z.string().min(10),
  instructionsNative: z.string().min(10),
  toTarget: z.array(TranslationItemSchema).min(3).max(6),
  toNative: z.array(TranslationItemSchema).min(3).max(6),
}).refine(
  (block) => {
    // Must have a mix of difficulties
    const allItems = [...block.toTarget, ...block.toNative];
    const difficulties = new Set(allItems.map((i) => i.difficulty));
    return difficulties.size >= 2;
  },
  { message: 'Translation challenge must include at least 2 different difficulty levels' },
);

// =============================================================================
// 5. Conjugation Spotlight
// =============================================================================

export const ConjugationVerbSchema = z.object({
  infinitive: z.string().min(2),
  translation: z.string().min(2),
  tense: z.string().min(3),
  conjugations: z.record(z.string(), z.string()).refine((c) => Object.keys(c).length >= 5, {
    message: 'Conjugation table must have at least 5 person forms',
  }),
  isIrregular: z.boolean(),
});

export const ConjugationExerciseSchema = z.object({
  sentence: z
    .string()
    .min(5)
    .refine((s) => s.includes('___'), {
      message: 'Exercise sentence must contain "___" for the blank',
    }),
  hint: z.string().min(3),
  answer: z.string().min(1),
  verb: z.string().min(2),
});

export const ConjugationSpotlightBlockSchema = BlockBaseSchema.extend({
  type: z.literal('conjugation_spotlight'),
  verbs: z.array(ConjugationVerbSchema).min(2).max(4),
  exercises: z.array(ConjugationExerciseSchema).min(4).max(8),
}).refine(
  (block) => {
    // Every exercise verb must appear in the verbs array
    const infinitives = block.verbs.map((v) => v.infinitive.toLowerCase());
    return block.exercises.every((e) => infinitives.includes(e.verb.toLowerCase()));
  },
  { message: 'Every exercise must reference a verb from the verbs array' },
);

// =============================================================================
// 6. Reading Comprehension
// =============================================================================

export const ReadingQuestionSchema = z
  .object({
    question: z.string().min(5),
    questionNative: z.string().min(5),
    answer: z.string().min(1),
    type: z.enum(['open', 'true_false', 'multiple_choice']),
    options: z.array(z.string().min(1)).min(2).max(4).optional(),
  })
  .refine(
    (q) => {
      if (q.type === 'multiple_choice') return q.options !== undefined && q.options.length >= 2;
      return true;
    },
    { message: 'Multiple choice questions must have at least 2 options' },
  );

export const ReadingComprehensionBlockSchema = BlockBaseSchema.extend({
  type: z.literal('reading_comprehension'),
  text: z.string().min(100).max(1500),
  textType: z.string().min(2),
  questions: z.array(ReadingQuestionSchema).min(3).max(7),
}).refine(
  (block) => {
    // Must have a mix of question types
    const types = new Set(block.questions.map((q) => q.type));
    return types.size >= 2;
  },
  { message: 'Reading comprehension must include at least 2 different question types' },
);

// =============================================================================
// 7. Matching Exercise
// =============================================================================

export const MatchingExerciseBlockSchema = BlockBaseSchema.extend({
  type: z.literal('matching_exercise'),
  instructions: z.string().min(10),
  instructionsNative: z.string().min(10),
  matchType: z.enum(['word_translation', 'sentence_halves', 'question_response', 'word_category']),
  columnA: z.array(z.string().min(1)).min(5).max(10),
  columnB: z.array(z.string().min(1)).min(5).max(10),
  correctPairs: z
    .array(z.tuple([z.number().int().min(0), z.number().int().min(0)]))
    .min(5)
    .max(10),
})
  .refine((block) => block.columnA.length === block.columnB.length, {
    message: 'columnA and columnB must have equal length',
  })
  .refine((block) => block.correctPairs.length === block.columnA.length, {
    message: 'correctPairs length must match column length',
  })
  .refine(
    (block) => {
      // Every index in correctPairs must be in range
      return block.correctPairs.every(
        ([a, b]) => a < block.columnA.length && b < block.columnB.length,
      );
    },
    { message: 'All pair indices must be within column bounds' },
  );

// =============================================================================
// 8. Scenario Prompts
// =============================================================================

export const ScenarioSchema = z.object({
  situation: z.string().min(15),
  situationTarget: z.string().min(10),
  emoji: z.string().min(1).max(4),
  suggestedAnswer: z.string().min(10),
  helpfulVocabulary: z.array(z.string().min(1)).min(3).max(6),
});

export const ScenarioPromptsBlockSchema = BlockBaseSchema.extend({
  type: z.literal('scenario_prompts'),
  instructions: z.string().min(10),
  instructionsNative: z.string().min(10),
  scenarios: z.array(ScenarioSchema).min(2).max(5),
});

// =============================================================================
// 9. Grammar Explanation
// =============================================================================

export const GrammarExampleSchema = z.object({
  target: z.string().min(5),
  native: z.string().min(5),
  highlight: z.string().min(1),
});

export const GrammarPracticeItemSchema = z.object({
  question: z.string().min(5),
  answer: z.string().min(1),
  hint: z.string().min(3),
});

export const GrammarExplanationBlockSchema = BlockBaseSchema.extend({
  type: z.literal('grammar_explanation'),
  topic: z.string().min(3).max(200),
  topicNative: z.string().min(3).max(200),
  explanation: z.string().min(30),
  pattern: z.string().optional(),
  examples: z.array(GrammarExampleSchema).min(3).max(5),
  commonMistakes: z.array(z.string().min(10)).min(2).max(3),
  practice: z.array(GrammarPracticeItemSchema).min(2).max(3),
});

// =============================================================================
// Discriminated union of all block schemas
// =============================================================================

// NOTE: Using z.union instead of z.discriminatedUnion because several block
// schemas use .refine(), which wraps them in ZodEffects. Zod 3.24's
// discriminatedUnion can't read the 'type' discriminator from ZodEffects,
// causing a runtime crash in Deno ("Cannot read properties of undefined
// (reading 'type')"). z.union is functionally identical — just slightly
// less optimized for error messages.
export const BlockSchema = z.union([
  GuidedConversationBlockSchema,
  VocabularyGridBlockSchema,
  FillInTheBlankBlockSchema,
  TranslationChallengeBlockSchema,
  ConjugationSpotlightBlockSchema,
  ReadingComprehensionBlockSchema,
  MatchingExerciseBlockSchema,
  ScenarioPromptsBlockSchema,
  GrammarExplanationBlockSchema,
]);

// =============================================================================
// Back matter schemas
// =============================================================================

export const VerbGlossaryEntrySchema = z.object({
  infinitive: z.string().min(2),
  translation: z.string().min(2),
  tenses: z
    .array(
      z.object({
        name: z.string().min(3),
        conjugations: z.record(z.string(), z.string()).refine((c) => Object.keys(c).length >= 5, {
          message: 'Conjugation must have at least 5 person forms',
        }),
      }),
    )
    .min(1)
    .max(3),
  isIrregular: z.boolean(),
  usageNote: z.string().optional(),
});

export const CulturalNoteSchema = z.object({
  title: z.string().min(3).max(100),
  titleNative: z.string().min(3).max(100),
  emoji: z.string().min(1).max(4),
  content: z.string().min(50).max(1500),
  relatedTo: z.string().min(2),
});

export const AnswerKeyEntrySchema = z.object({
  blockId: z.string().uuid(),
  blockType: z.enum([
    'guided_conversation',
    'vocabulary_grid',
    'fill_in_the_blank',
    'translation_challenge',
    'conjugation_spotlight',
    'reading_comprehension',
    'matching_exercise',
    'scenario_prompts',
    'grammar_explanation',
  ]),
  blockTitle: z.string().min(1),
  answers: z.array(z.string().min(1)).min(1),
});

export const CoverDataSchema = z.object({
  displayName: z.string().min(1),
  targetLanguageName: z.string().min(1),
  nativeLanguageName: z.string().min(1),
  level: z.string().min(2),
  month: z.string().min(2),
  year: z.number().int().min(2024).max(2100),
  theme: z.string().min(2),
  endLevel: z.string().min(2).optional(),
  targetLanguageCode: z.enum(['fr', 'es', 'pt', 'it', 'en', 'de']).optional(),
});

// =============================================================================
// Welcome page schema
// =============================================================================

export const WelcomePageSchema = z.object({
  greeting: z.string().min(3).max(100),
  summary: z.string().min(30).max(800),
  statistic: z.string().min(20).max(500),
});

// =============================================================================
// Complete workbook content schema
// =============================================================================

export const WorkbookContentSchema = z.object({
  title: z.string().min(5),
  theme: z.string().min(2),
  blocks: z.array(BlockSchema).min(6).max(65),
  answerKey: z.array(AnswerKeyEntrySchema).min(1),
  verbGlossary: z.array(VerbGlossaryEntrySchema).min(1),
  culturalNotes: z.array(CulturalNoteSchema).min(2).max(8),
  coverData: CoverDataSchema,
  welcomePage: WelcomePageSchema.optional(),
});
